import { INPUTS, type Sparse } from './features'

/**
 * Сеть оценки позиции: один скрытый слой, на выходе — шансы на победу
 * каждого места за столом.
 *
 * Выход — распределение по местам, а не одно число «мой шанс». Сумма шансов
 * всех равна единице, и сеть это знает с рождения; а при обучении позиция
 * одного игрока учит и оценку остальных. Пустые места (партия вдвоём)
 * из распределения исключаются.
 *
 * Библиотек нет намеренно: прямой проход — два умножения, обратный — ещё
 * два, и эта же арифметика без изменений повторяется на PHP. Любой рантайм
 * (ONNX, tfjs) пришлось бы тащить и в браузер, и на сервер ради сотни строк.
 */

export const SEATS = 4

export type Model = {
  inputs: number
  hidden: number
  /** Вход ⇒ скрытый слой, по строке на вход: разреженный вход берёт строки целиком. */
  w1: Float32Array
  b1: Float32Array
  /** Скрытый слой ⇒ выход, по строке на нейрон. */
  w2: Float32Array
  b2: Float32Array
}

export type Pass = {
  /**
   * Скрытый слой после ReLU — в float64, хотя веса float32: так же считает
   * PHP (App\Domain\Ludo\Bot\Net\Model), и оценки совпадают до последних
   * знаков, а не «примерно». Копить во float32 значило бы округлять каждое
   * сложение, и сверка двух реализаций шла бы с допуском, за которым
   * спряталась бы и настоящая ошибка.
   */
  hidden: Float64Array
  /** Шансы мест; у пустых — 0. */
  odds: Float64Array
}

export function createModel(hidden: number, random: () => number): Model {
  // Разброс под число одновременно горящих входов (десятки), а не под их
  // общее число: иначе стартовые веса были бы слишком малы и сеть долго
  // «просыпалась».
  const spread = (scale: number) => () => (random() * 2 - 1) * scale

  return {
    inputs: INPUTS,
    hidden,
    w1: Float32Array.from({ length: INPUTS * hidden }, spread(0.15)),
    b1: new Float32Array(hidden),
    w2: Float32Array.from({ length: hidden * SEATS }, spread(0.15)),
    b2: new Float32Array(SEATS),
  }
}

/** @param present сколько мест за столом занято: места 0..present-1. */
export function forward(model: Model, x: Sparse, present: number): Pass {
  const { hidden: size, w1, b1, w2, b2 } = model
  const hidden = Float64Array.from(b1)

  for (let k = 0; k < x.index.length; k++) {
    const row = x.index[k] * size
    const v = x.value[k]

    for (let j = 0; j < size; j++) hidden[j] += w1[row + j] * v
  }

  for (let j = 0; j < size; j++) if (hidden[j] < 0) hidden[j] = 0

  const logits = new Float64Array(SEATS)

  for (let s = 0; s < present; s++) {
    let sum = b2[s]

    for (let j = 0; j < size; j++) sum += hidden[j] * w2[j * SEATS + s]

    logits[s] = sum
  }

  return { hidden, odds: softmax(logits, present) }
}

/**
 * Шаг обучения: подвинуть оценку позиции x к target — распределению
 * «кто победит», посчитанному позже (см. обучение, λ-возвраты).
 *
 * Потеря — перекрёстная энтропия softmax, поэтому градиент по выходу
 * просто odds − target.
 */
export function learn(
  model: Model,
  x: Sparse,
  pass: Pass,
  target: ArrayLike<number>,
  present: number,
  rate: number,
): void {
  const { hidden: size, w1, b1, w2, b2 } = model
  const out = new Float64Array(SEATS)

  for (let s = 0; s < present; s++) out[s] = pass.odds[s] - target[s]

  const back = new Float64Array(size)

  for (let j = 0; j < size; j++) {
    if (pass.hidden[j] <= 0) continue

    let sum = 0

    for (let s = 0; s < present; s++) sum += w2[j * SEATS + s] * out[s]

    back[j] = sum
  }

  for (let j = 0; j < size; j++) {
    const h = pass.hidden[j]

    if (h === 0) continue

    for (let s = 0; s < present; s++) w2[j * SEATS + s] -= rate * h * out[s]
  }

  for (let s = 0; s < present; s++) b2[s] -= rate * out[s]

  for (let k = 0; k < x.index.length; k++) {
    const row = x.index[k] * size
    const v = rate * x.value[k]

    for (let j = 0; j < size; j++) if (back[j] !== 0) w1[row + j] -= v * back[j]
  }

  for (let j = 0; j < size; j++) b1[j] -= rate * back[j]
}

function softmax(logits: Float64Array, present: number): Float64Array {
  let top = Number.NEGATIVE_INFINITY

  for (let s = 0; s < present; s++) top = Math.max(top, logits[s])

  const odds = new Float64Array(SEATS)
  let total = 0

  for (let s = 0; s < present; s++) {
    odds[s] = Math.exp(logits[s] - top)
    total += odds[s]
  }

  for (let s = 0; s < present; s++) odds[s] /= total

  return odds
}

/** Модель в файле: веса — base64 от float32, чтобы файл был вчетверо меньше JSON-чисел. */
export type ModelFile = {
  version: 1
  inputs: number
  hidden: number
  w1: string
  b1: string
  w2: string
  b2: string
  /** Чем и сколько учили — для людей, сеть это не читает. */
  meta?: Record<string, unknown>
}

export function saveModel(model: Model, meta?: Record<string, unknown>): ModelFile {
  return {
    version: 1,
    inputs: model.inputs,
    hidden: model.hidden,
    w1: pack(model.w1),
    b1: pack(model.b1),
    w2: pack(model.w2),
    b2: pack(model.b2),
    meta,
  }
}

export function loadModel(file: ModelFile): Model {
  if (file.inputs !== INPUTS) {
    throw new Error(`модель на ${file.inputs} входов, а кодировка даёт ${INPUTS}`)
  }

  return {
    inputs: file.inputs,
    hidden: file.hidden,
    w1: unpack(file.w1),
    b1: unpack(file.b1),
    w2: unpack(file.w2),
    b2: unpack(file.b2),
  }
}

function pack(values: Float32Array): string {
  const bytes = new Uint8Array(values.buffer, values.byteOffset, values.byteLength)
  let binary = ''

  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])

  return btoa(binary)
}

function unpack(text: string): Float32Array {
  const binary = atob(text)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)

  return new Float32Array(bytes.buffer)
}
